import { useState } from 'react';
import { useNavigate } from 'react-router-dom';
import { BackButton } from '@/components/shared/BackButton';
import { useQuery, useMutation, useQueryClient } from '@tanstack/react-query';
import { fetchFilters, updateFilter, deleteFilter, type FilterRule } from '../../lib/inboxFiltersApi';
import { QueryErrorState } from '@/components/shared/QueryErrorState';

export default function InboxFiltersPage() {
  const navigate = useNavigate();
  const qc = useQueryClient();
  const [deleteConfirm, setDeleteConfirm] = useState<string | null>(null);

  const { data: rules = [], isLoading, isError, refetch } = useQuery({
    queryKey: ['inbox-filters'],
    queryFn: fetchFilters,
    // Cross-device freshness. `invalidateQueries` only refreshes the device that made the
    // change, and the inbox module emits no socket events, so a second device sat on its cached
    // list until the person refreshed by hand (seen: a signature added on a phone never appeared
    // on the open desktop tab). Polling only runs while the tab is visible — react-query's
    // `refetchIntervalInBackground` defaults to false — so an idle background tab costs nothing.
    // 15s, not 60: a minute is long enough that someone adding a signature on a phone and
    // turning to the desktop reads it as broken and refreshes by hand anyway.
    refetchInterval: 15_000,
    // Global staleTime is 60s (main.tsx:57), which would make a focus refetch a no-op for
    // the first minute. These lists are small and change rarely — always refetch.
    staleTime: 0,
  });

  const toggleMutation = useMutation({
    mutationFn: ({ id, enabled }: { id: string; enabled: boolean }) =>
      updateFilter(id, { enabled }),
    onSuccess: () => qc.invalidateQueries({ queryKey: ['inbox-filters'] }),
  });

  const deleteMutation = useMutation({
    mutationFn: deleteFilter,
    onSuccess: () => {
      setDeleteConfirm(null);
      qc.invalidateQueries({ queryKey: ['inbox-filters'] });
    },
  });

  function describeConditions(rule: FilterRule): string {
    if (!rule.conditions.length) return '—';
    const first = rule.conditions[0]!;
    const label = first.field.replace(/_/g, ' ');
    const extra = rule.conditions.length > 1 ? ` + ${rule.conditions.length - 1} more` : '';
    return `${label} ${first.operator.replace(/_/g, ' ')} "${first.value}"${extra}`;
  }

  function describeActions(rule: FilterRule): string {
    return rule.actions.map((a) => a.type.replace(/_/g, ' ')).join(', ');
  }

  return (
    <div style={{
      maxWidth: 760, margin: '0 auto',
      padding: 'clamp(20px, 5vw, 40px) clamp(14px, 4vw, 24px)',
      fontFamily: "'IBM Plex Sans', sans-serif",
    }}>
      {/* Was an unlabelled arrow wired to a fixed destination. These pages are opened from the
          inbox, from the Email settings hub and from Settings, so a fixed target sent people
          somewhere they had never been. Back means back — same control as every other page. */}
      <BackButton onClick={() => navigate(-1)} />

      <div style={{ display: 'flex', flexWrap: 'wrap', alignItems: 'center', gap: 16, marginBottom: 32 }}>
        <div style={{ flex: 1 }}>
          <h1 style={{ fontSize: 20, fontWeight: 600, color: 'var(--text-primary)', margin: 0 }}>
            Filters &amp; Rules
          </h1>
          <p style={{ fontSize: 13, color: 'var(--text-muted)', margin: '2px 0 0' }}>
            Rules run automatically after each email sync
          </p>
        </div>
        <button
          onClick={() => navigate('/barrister/inbox/settings/filters/new')}
          style={{
            padding: '8px 18px', borderRadius: 6,
            background: '#1e3a5f', color: 'white',
            border: 'none', cursor: 'pointer',
            fontSize: 13, fontWeight: 500,
          }}
        >
          + New rule
        </button>
      </div>

      {isLoading ? (
        <div style={{ textAlign: 'center', color: 'var(--text-muted)', padding: 40 }}>Loading…</div>
      ) : isError ? (
        /* A failed fetch has no rules either, and would have read as "No filter rules yet" —
           inviting someone to recreate rules that already exist. */
        <QueryErrorState what="your filter rules" onRetry={() => void refetch()} />
      ) : rules.length === 0 ? (
        <div style={{
          border: '1px dashed #d1d5db', borderRadius: 8,
          padding: '48px 24px', textAlign: 'center', color: 'var(--text-muted)',
        }}>
          <div style={{ fontSize: 32, marginBottom: 12 }}>≡</div>
          <div style={{ fontWeight: 500, marginBottom: 6 }}>No filter rules yet</div>
          <div style={{ fontSize: 13 }}>
            Create a rule to automatically label, archive, or link incoming emails.
          </div>
        </div>
      ) : (
        <div style={{ display: 'flex', flexDirection: 'column', gap: 2 }}>
          {rules.map((rule) => (
            <div
              key={rule.id}
              style={{
                border: '1px solid var(--border-default)', borderRadius: 8,
                padding: '14px 18px', background: 'var(--surface-card)',
                display: 'flex', flexWrap: 'wrap', alignItems: 'flex-start', gap: 14,
                opacity: rule.enabled ? 1 : 0.55,
              }}
            >
              <div style={{ flex: 1, minWidth: 0 }}>
                <div style={{
                  fontWeight: 500, fontSize: 14, color: 'var(--text-primary)', marginBottom: 4,
                  display: 'flex', alignItems: 'center', gap: 8,
                }}>
                  {rule.name}
                  {!rule.enabled && (
                    <span style={{
                      fontSize: 10, background: 'var(--surface-sunken)', color: 'var(--text-muted)',
                      padding: '1px 6px', borderRadius: 10, fontWeight: 400,
                    }}>
                      disabled
                    </span>
                  )}
                </div>
                <div style={{ fontSize: 12, color: 'var(--text-muted)', marginBottom: 2 }}>
                  <strong>If:</strong> {describeConditions(rule)}
                </div>
                <div style={{ fontSize: 12, color: 'var(--text-muted)', marginBottom: 2 }}>
                  <strong>Then:</strong> {describeActions(rule)}
                </div>
                <div style={{ fontSize: 11, color: 'var(--text-subtle)', marginTop: 4 }}>
                  {rule.hitCount} match{rule.hitCount !== 1 ? 'es' : ''} ·{' '}
                  {rule.conditionLogic === 'all' ? 'Match all conditions' : 'Match any condition'}
                </div>
              </div>

              <div style={{ display: 'flex', alignItems: 'center', gap: 10, flexShrink: 0 }}>
                <label style={{ display: 'flex', alignItems: 'center', cursor: 'pointer' }}>
                  <input
                    type="checkbox"
                    checked={rule.enabled}
                    onChange={(e) => toggleMutation.mutate({ id: rule.id, enabled: e.target.checked })}
                    style={{ width: 16, height: 16, cursor: 'pointer' }}
                  />
                </label>
                <button
                  onClick={() => navigate(`/barrister/inbox/settings/filters/${rule.id}/edit`)}
                  style={{
                    background: 'none', border: '1px solid var(--border-default)',
                    borderRadius: 4, padding: '8px 12px', cursor: 'pointer',
                    fontSize: 12, color: 'var(--text-primary)',
                  }}
                >
                  Edit
                </button>
                {deleteConfirm === rule.id ? (
                  <div style={{ display: 'flex', gap: 4 }}>
                    <button
                      onClick={() => deleteMutation.mutate(rule.id)}
                      style={{
                        background: '#dc2626', color: 'white',
                        border: 'none', borderRadius: 4, padding: '8px 12px',
                        cursor: 'pointer', fontSize: 12,
                      }}
                    >
                      Confirm
                    </button>
                    <button
                      onClick={() => setDeleteConfirm(null)}
                      style={{
                        background: 'none', border: '1px solid var(--border-default)',
                        borderRadius: 4, padding: '8px 12px',
                        cursor: 'pointer', fontSize: 12, color: 'var(--text-primary)',
                      }}
                    >
                      Cancel
                    </button>
                  </div>
                ) : (
                  <button
                    onClick={() => setDeleteConfirm(rule.id)}
                    style={{
                      background: 'none', border: 'none',
                      cursor: 'pointer', fontSize: 14, color: 'var(--text-subtle)',
                    }}
                  >
                    ✕
                  </button>
                )}
              </div>
            </div>
          ))}
        </div>
      )}
    </div>
  );
}
